// Tutorial completion states
export type TutorialCompletionState = 'Incomplete' | 'Pass' | 'Super';

export interface Challenge {
  id: number;
  completed: boolean;
  completionState: TutorialCompletionState; // New field for 3-state system
  isSpecial: boolean; // Keep for backward compatibility
  completedAt?: Date;
}

export interface ConstellationProgress {
  name: string;
  totalChallenges: number;
  completedChallenges: number;
  superChallenges: number; // New: count of Super completions
  challenges: Challenge[];
  isUnlocked: boolean;
  unlockedAt?: Date;
}

export interface TutorialProgressData {
  constellations: { [key: string]: ConstellationProgress };
  lastUpdated: number;
}

// Progressive unlocking order from original game
const UNLOCK_SEQUENCE = [
  'Bear', 'Crane', 'Tree', 'Monkey', 'Wolf', 'Rhino',
  'Snake', 'Mushroom', 'Penguin', 'Frog', 'Goat', 'Octopus'
];

// Order traversed by the railroad navigation arrows on the star map.
// Includes Virus as the final stop, which UNLOCK_SEQUENCE intentionally
// omits (Virus is unlocked lazily via markUnlocked once Octopus is cleared).
export const RAILROAD_SEQUENCE = [
  'Bear', 'Crane', 'Tree', 'Monkey', 'Wolf', 'Rhino',
  'Snake', 'Mushroom', 'Penguin', 'Frog', 'Goat', 'Octopus', 'Virus'
] as const;

// Tutorial counts for each constellation (from original game)
const TUTORIAL_COUNTS = {
  'Bear': 5,
  'Crane': 3, 
  'Tree': 3,
  'Monkey': 5,
  'Wolf': 3,
  'Rhino': 5,
  'Snake': 4,
  'Mushroom': 4,
  'Penguin': 4,
  'Frog': 5,
  'Goat': 4,
  'Octopus': 6,
  'Virus': 3 // Special case - not in unlock sequence but has tutorials
};

class TutorialProgressService {
  private readonly STORAGE_KEY = 'zodiac-tutorial-progress';
  private progressData: TutorialProgressData;

  constructor() {
    this.progressData = this.loadProgress();
  }

  private loadProgress(): TutorialProgressData {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        // Check if we need to migrate data (e.g., Bear changed from 3 to 5 tutorials)
        return this.migrateProgressData(data);
      }
    } catch (error) {
    }

    // Initialize default progress
    return this.createDefaultProgress();
  }

  private migrateProgressData(data: TutorialProgressData): TutorialProgressData {
    let needsUpdate = false;

    // Check each constellation for tutorial count mismatches
    Object.entries(TUTORIAL_COUNTS).forEach(([name, expectedCount]) => {
      const constellation = data.constellations[name];
      if (constellation && constellation.totalChallenges !== expectedCount) {
        
        // Update the total challenges
        constellation.totalChallenges = expectedCount;
        
        // Add missing challenges if needed
        while (constellation.challenges.length < expectedCount) {
          const newIndex = constellation.challenges.length;
          constellation.challenges.push({
            id: newIndex + 1, // Assuming id is sequential
            completed: false,
            completionState: 'Incomplete',
            isSpecial: newIndex === expectedCount - 1 // Last challenge is special
          });
        }
        
        // Remove extra challenges if needed (shouldn't happen but just in case)
        if (constellation.challenges.length > expectedCount) {
          constellation.challenges = constellation.challenges.slice(0, expectedCount);
        }
        
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      this.saveProgressData(data);
    }

    return data;
  }

  private saveProgressData(data: TutorialProgressData): void {
    try {
      data.lastUpdated = Date.now();
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
    }
  }

  private createDefaultProgress(): TutorialProgressData {
    const constellations: { [key: string]: ConstellationProgress } = {};

    Object.entries(TUTORIAL_COUNTS).forEach(([name, totalChallenges]) => {
      const challenges: Challenge[] = [];
      
      // Create challenges for this constellation
      for (let i = 0; i < totalChallenges; i++) {
        challenges.push({
          id: i + 1, // Assuming id is sequential
          completed: false,
          completionState: 'Incomplete',
          isSpecial: i === totalChallenges - 1 // Last challenge is special
        });
      }

      constellations[name] = {
        name,
        totalChallenges,
        completedChallenges: 0,
        superChallenges: 0,
        challenges,
        isUnlocked: name === 'Bear', // Only Bear starts unlocked
        unlockedAt: name === 'Bear' ? new Date() : undefined
      };
    });

    return {
      constellations,
      lastUpdated: Date.now()
    };
  }

  private saveProgress(): void {
    this.saveProgressData(this.progressData);
  }

  public getConstellationProgress(name: string): ConstellationProgress | null {
    return this.progressData.constellations[name] || null;
  }

  public getAllProgress(): { [key: string]: ConstellationProgress } {
    return { ...this.progressData.constellations };
  }

  public hasAnyCompletedTutorials(): boolean {
    const allProgress = this.getAllProgress();
    for (const constellationName in allProgress) {
      const constellation = allProgress[constellationName];
      if (constellation.completedChallenges > 0) {
        return true;
      }
    }
    return false;
  }

  public getNextIncompleteTutorial(): { constellation: string; starIndex: number } | null {
    // Go through unlock sequence to find first incomplete tutorial
    for (const constellationName of UNLOCK_SEQUENCE) {
      const progress = this.getConstellationProgress(constellationName);
      if (progress && progress.isUnlocked) {
        // Find first incomplete challenge in this constellation
        const incompleteChallenge = progress.challenges.find(c => !c.completed);
        if (incompleteChallenge) {
          return {
            constellation: constellationName,
            starIndex: incompleteChallenge.id - 1  // Convert from 1-based ID to 0-based index
          };
        }
      }
    }
    return null; // All tutorials completed
  }

  public completeChallenge(constellationName: string, challengeId: number): boolean {
    const constellation = this.progressData.constellations[constellationName];
    if (!constellation || !constellation.isUnlocked) {
      return false;
    }

    const challenge = constellation.challenges.find(c => c.id === challengeId);
    if (!challenge || challenge.completed) {
      return false;
    }

    // Mark challenge as completed
    challenge.completed = true;
    challenge.completionState = 'Pass'; // Default to Pass
    constellation.completedChallenges++;
    
    if (challenge.isSpecial) {
      challenge.completionState = 'Super';
      constellation.superChallenges++;
    }

    // Check if constellation meets unlock criteria and unlock next
    if (this.shouldUnlockNextConstellation(constellationName, constellation)) {
      this.unlockNextConstellation(constellationName);
    }

    this.saveProgress();
    return true;
  }

  /**
   * Complete a tutorial with specific victory type (regular or secret)
   * @param constellationName The constellation name
   * @param tutorialIndex The tutorial index (0-based)
   * @param victoryType 'regular' or 'secret'
   * @returns Whether the tutorial was successfully completed
   */
  public completeTutorial(constellationName: string, tutorialIndex: number, victoryType: 'regular' | 'secret'): boolean {
    const constellation = this.progressData.constellations[constellationName];
    if (!constellation || !constellation.isUnlocked) {
      return false;
    }

    const challenge = constellation.challenges[tutorialIndex];
    if (!challenge) {
      return false;
    }

    // Update completion based on victory type
    if (!challenge.completed) {
      // First time completing this tutorial
      challenge.completed = true;
      constellation.completedChallenges++;
      
      if (victoryType === 'secret') {
        challenge.completionState = 'Super';
        constellation.superChallenges++;
      } else {
        challenge.completionState = 'Pass';
      }
    } else if (victoryType === 'secret' && challenge.completionState === 'Pass') {
      // Upgrading from regular to secret victory
      challenge.completionState = 'Super';
      constellation.superChallenges++;
    }

    // Check if constellation meets unlock criteria and unlock next
    if (this.shouldUnlockNextConstellation(constellationName, constellation)) {
      this.unlockNextConstellation(constellationName);
    }

    this.saveProgress();
    return true;
  }

  /**
   * Determines if a constellation has met the criteria to unlock the next constellation
   * @param constellationName Name of the constellation
   * @param constellation The constellation progress object
   * @returns True if next constellation should be unlocked
   */
  private shouldUnlockNextConstellation(constellationName: string, constellation: ConstellationProgress): boolean {
    // Tree only has 3 tutorials and was already given a permissive 2-of-3 rule.
    if (constellationName === 'Tree') {
      return constellation.completedChallenges >= 2;
    }

    // Everywhere else: any 3 tutorials unlock the path forward. Min-guard so
    // a constellation with fewer than 3 still requires all of them complete.
    const threshold = Math.min(3, constellation.totalChallenges);
    return constellation.completedChallenges >= threshold;
  }

  private unlockNextConstellation(completedConstellation: string): void {
    const currentIndex = UNLOCK_SEQUENCE.indexOf(completedConstellation);
    if (currentIndex >= 0 && currentIndex < UNLOCK_SEQUENCE.length - 1) {
      const nextConstellation = UNLOCK_SEQUENCE[currentIndex + 1];
      const next = this.progressData.constellations[nextConstellation];
      if (next) {
        next.isUnlocked = true;
        next.unlockedAt = new Date();
      }
    }
  }

  // Surfaces the existing unlock predicate for the railroad arrow UI.
  public canAdvanceFrom(name: string): boolean {
    const c = this.progressData.constellations[name];
    if (!c) return false;
    return this.shouldUnlockNextConstellation(name, c);
  }

  public isConstellationUnlocked(name: string): boolean {
    const c = this.progressData.constellations[name];
    return !!c && c.isUnlocked;
  }

  // Used by the railroad to lazily unlock constellations outside UNLOCK_SEQUENCE
  // (Virus) once the player advances past Octopus.
  public markUnlocked(name: string): void {
    const c = this.progressData.constellations[name];
    if (!c || c.isUnlocked) return;
    c.isUnlocked = true;
    c.unlockedAt = new Date();
    this.saveProgress();
  }

  public getConstellationOpacity(name: string): number {
    const constellation = this.progressData.constellations[name];
    if (!constellation || !constellation.isUnlocked) {
      return 0; // Invisible if not unlocked
    }

    // Calculate weighted opacity based on challenge states
    let incompleteCount = 0;
    let completeCount = 0;
    let superCount = 0;

    constellation.challenges.forEach(challenge => {
      if (challenge.completionState === 'Incomplete') {
        incompleteCount++;
      } else if (challenge.completionState === 'Super') {
        superCount++;
      } else { // Pass
        completeCount++;
      }
    });

    const total = constellation.totalChallenges;
    
    // NEW BRIGHTNESS SYSTEM:
    // Base: 1% (when all incomplete)  
    // Each tutorial contributes either regular (5%/total) OR secret (20%/total)
    // Max 6% if all regular (1% + 5%), Max 21% if all secret (1% + 20%)
    
    const baseOpacity = 1; // Start at 1%
    const completedTutorials = completeCount + superCount;
    
    if (completedTutorials === 0) {
      return baseOpacity / 100; // Just base opacity
    }
    
    // Each tutorial contributes based on its completion type
    const regularPerTutorial = 5 / total; // Regular tutorials contribute 5%/total each
    const secretPerTutorial = 20 / total; // Secret tutorials contribute 20%/total each
    
    const progressBonus = (completeCount * regularPerTutorial) + (superCount * secretPerTutorial);
    const totalOpacity = baseOpacity + progressBonus;
    
    
    return Math.min(totalOpacity / 100, 1.0); // Convert to decimal, cap at 100%
  }

  public resetProgress(): void {
    this.progressData = this.createDefaultProgress();
    this.saveProgress();
    
    // Clear tutorial intro localStorage keys to reset popup visibility
    this.clearTutorialIntroSettings();
  }

  private clearTutorialIntroSettings(): void {
    // Clear the global tutorial intro disabled setting
    localStorage.removeItem('tutorialIntroDisabled');
    
    // Clear all tutorial-specific intro seen settings
    // Get all localStorage keys and remove ones that match the tutorial intro pattern
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('tutorialIntroSeen-')) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
  }

  // Debug method for testing
  public unlockAllConstellations(): void {
    Object.values(this.progressData.constellations).forEach(constellation => {
      constellation.isUnlocked = true;
      constellation.unlockedAt = new Date();
    });
    this.saveProgress();
  }
}

export default new TutorialProgressService(); 